// Streams the diagnosis via SSE. While streaming, renders raw text as a plain
// flowing paragraph so the browser never has to reflow block structure
// mid-stream. On completion, cross-fades to the fully structured layout
// (paragraph + numbered risk cards + question callout). This eliminates the
// visual jag caused by risk blocks popping in and reflowing the paragraph
// above them while the stream is still running.

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Loader2, AlertCircle } from 'lucide-react';
import { streamDiagnose } from '../lib/api';
import { track } from '../lib/mixpanel';

type DiagnosisContentProps = { text: string; onCausalSpineRead: () => void };

function DiagnosisContent({ text, onCausalSpineRead }: DiagnosisContentProps) {
  const causalSpineRef = useRef<HTMLParagraphElement | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const el = causalSpineRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          onCausalSpineRead();
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onCausalSpineRead]);

  const trimmed = text.trim();
  const firstRiskMatch = trimmed.match(/^\s*1\.\s+/m);
  if (!firstRiskMatch) {
    return <p>{trimmed}</p>;
  }

  const firstRiskIdx = trimmed.indexOf(firstRiskMatch[0]);
  const paragraph = trimmed.slice(0, firstRiskIdx).trim();
  const rest = trimmed.slice(firstRiskIdx);

  const qMatch = rest.match(/\*([^*]+\?)\*/);
  const beforeQuestion = qMatch ? rest.slice(0, qMatch.index!) : rest;
  const question = qMatch ? qMatch[1] : '';

  const risks: string[] = [];
  const riskRe = /^\s*([1-3])\.\s+(.+)$/gm;
  let m: RegExpExecArray | null;
  let lastRiskEnd = 0;
  while ((m = riskRe.exec(beforeQuestion)) !== null) {
    risks.push(m[2].trim());
    lastRiskEnd = m.index + m[0].length;
  }

  const causalSpine = beforeQuestion.slice(lastRiskEnd).trim();

  return (
    <>
      <p className="mb-6">{paragraph}</p>
      {risks.length > 0 && (
        <ol className="space-y-2 mb-6 list-none p-0">
          {risks.map((r, i) => (
            <li
              key={i}
              className="grid grid-cols-[36px_1fr] gap-4 px-5 py-4 bg-white border border-border border-l-[3px] border-l-clay rounded-md"
            >
              <span className="font-display font-bold text-[18px] text-clay-deep tracking-[-0.01em] leading-[1.4]">
                0{i + 1}
              </span>
              <span className="text-[15px] leading-[1.7] text-ink-soft">{r}</span>
            </li>
          ))}
        </ol>
      )}
      {causalSpine && (
        <p ref={causalSpineRef} className="mb-6 text-[15px] leading-[1.7] text-ink-mute">{causalSpine}</p>
      )}
      {question && (
        <div className="mt-6 p-8 bg-green-700 text-paper rounded-lg">
          <em className="not-italic font-normal italic text-[19px] leading-[1.45] text-paper block">
            {question}
          </em>
        </div>
      )}
    </>
  );
}

interface Props {
  orgChart: string;
  hasFitResult: boolean;
  onComplete: (fullText: string) => void;
}

export default function DiagnosisStream({ orgChart, hasFitResult, onComplete }: Props) {
  const [text, setText] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let accumulated = '';
    const startedAt = Date.now();
    track('diagnosis_started', { // event 4
      org_chart_length: orgChart.length,
      has_fit_result: hasFitResult,
    });

    streamDiagnose(orgChart, {
      onDelta: (chunk) => {
        accumulated += chunk;
        setText(accumulated);
      },
      onDone: () => {
        setDone(true);
        track('diagnosis_completed', { // event 6
          duration_ms: Date.now() - startedAt,
          length_chars: accumulated.length,
        });
        onComplete(accumulated);
      },
      onError: (msg) => {
        setError(msg);
        track('diagnosis_failed', { message: msg });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgChart]);

  return (
    <section className="bg-paper-light border-b border-border py-24">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="text-[12px] font-semibold tracking-mega uppercase text-clay-deep mb-3">
          Diagnosis · Intelligence Tier lens
        </div>
        <h2 className="font-display text-[clamp(24px,3vw,32px)] font-semibold tracking-[-0.02em] leading-[1.15] text-ink max-w-[720px] mb-6">
          What we'd say to your board, if asked.
        </h2>

        <div className="flex items-center flex-wrap gap-3 text-[12px] text-stone-500 mb-6 tracking-[0.02em]">
          <span>Reading: Intelligence Tier lens</span>
          <span className="text-border-strong">·</span>
          <span>Written: just now</span>
          <span className="text-border-strong">·</span>
          <span>Reading time: 2 min</span>
        </div>

        {text.length === 0 && !error && (
          <div className="flex items-center gap-2 text-ink-mute text-sm">
            <Loader2 size={16} className="animate-spin" />
            <CycleText
              messages={[
                'Reading what you wrote…',
                'Mapping it through the Intelligence Tier lens…',
                'Finding the structural pattern…',
              ]}
            />
          </div>
        )}

        {text.length > 0 && (
          <div className="max-w-[720px]">
            <AnimatePresence mode="wait">
              {!done ? (
                // Streaming phase: plain flowing paragraph - no structural
                // reflow, no layout jumps. Cursor blinks at the end.
                <motion.p
                  key="streaming"
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="text-[16px] leading-[1.7] text-ink-soft"
                >
                  {text}
                  {!error && (
                    <span className="inline-block w-[2px] h-[1.1em] bg-ink-soft align-middle ml-0.5 animate-pulse" />
                  )}
                </motion.p>
              ) : (
                // Complete phase: cross-fade in the structured layout.
                <motion.div
                  key="structured"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-[16px] leading-[1.7] text-ink-soft"
                >
                  <DiagnosisContent
                    text={text}
                    onCausalSpineRead={() => track('diagnosis_read')} // event 5
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 text-[13px] text-clay">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {done && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 inline-flex items-center gap-2.5 text-[12px] text-stone-500 tracking-[0.04em] uppercase font-medium"
          >
            <span className="pulse-dot" />
            Going wider - three more lenses identified
          </motion.div>
        )}
      </div>
    </section>
  );
}


function CycleText({ messages }: { messages: string[] }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % messages.length), 2400);
    return () => clearInterval(t);
  }, [messages.length]);
  return <span>{messages[idx]}</span>;
}
